import { inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service'; // adjust path as needed
import { API_BASE_URL } from '../config/api-config';
import { backendErrorCode, backendErrorReason, extractBackendError } from '../http/backend-error';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.authService.accessToken;
    const sessionVersionAtRequest = this.authService.sessionVersion;
    const bypassAccessToken = this.isAccessTokenBypassedAuthEndpoint(req.url);
    if (!bypassAccessToken && this.authService.isCurrentSessionVersion?.(sessionVersionAtRequest) === false) {
      return throwError(() => new Error('Session changed before auth request'));
    }
    const shouldAttachAccessToken = accessToken && !bypassAccessToken;

    const authReq = shouldAttachAccessToken
      ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : req;

    return next.handle(authReq).pipe(
      catchError(error => {
        const isRefreshOrLogoutEndpoint = this.isRefreshOrLogoutEndpoint(req.url);

        const backend = extractBackendError(error);
        const code = backendErrorCode(backend);
        const reason = backendErrorReason(backend);

        const isTokenFailure =
          error.status === 401 &&
          (code === 'UNAUTHORIZED' || code === 'AUTH_TOKEN') &&
          (reason === 'TOKEN_EXPIRED' || reason === 'TOKEN_INVALID');

        if (isTokenFailure && !isRefreshOrLogoutEndpoint) {
          // A response from the previous login must not refresh or revoke the current one.
          if (this.authService.isCurrentSessionVersion?.(sessionVersionAtRequest) === false) {
            return throwError(() => error);
          }
          const currentToken = this.authService.accessToken;
          if (currentToken !== accessToken) {
            // A concurrent refresh may have already rotated this same session's bearer.
            // Retry only under proven session ownership; never replay an old login's request.
            return currentToken && this.authService.isCurrentSessionVersion?.(sessionVersionAtRequest) === true
              ? next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${currentToken}` } }))
              : throwError(() => error);
          }
          return this.handleUnauthorizedError(req, next, accessToken, sessionVersionAtRequest);
        }

        // Business 401 / 403: do not refresh.
        return throwError(() => error);
      })
    );
  }

  private handleUnauthorizedError(
    req: HttpRequest<any>,
    next: HttpHandler,
    failedAccessToken: string | null,
    sessionVersionAtRequest: number,
  ): Observable<HttpEvent<any>> {
    return this.authService.refreshAccessTokenCoalesced().pipe(
      switchMap(newToken => {
        // A refresh from an obsolete request must not replay that request as a newer login.
        if (this.authService.isCurrentSessionVersion?.(sessionVersionAtRequest) === false) {
          return throwError(() => new Error('Session changed while refreshing'));
        }
        if (!newToken?.accessToken) {
          return this.logoutAndFail('Refresh token failed', failedAccessToken, sessionVersionAtRequest);
        }
        const newReq = req.clone({ setHeaders: { Authorization: `Bearer ${newToken.accessToken}` } });
        return next.handle(newReq);
      }),
      catchError(() => {
        return this.logoutAndFail('Unable to refresh token', failedAccessToken, sessionVersionAtRequest);
      })
    );
  }

  private logoutAndFail(message: string, failedAccessToken: string | null, sessionVersionAtRequest: number): Observable<never> {
    if (this.authService.isCurrentSessionVersion?.(sessionVersionAtRequest) === false ||
        (this.authService.accessToken && this.authService.accessToken !== failedAccessToken)) {
      return throwError(() => new Error(message));
    }

    return from(this.authService.logout(false)).pipe(
      switchMap(() => throwError(() => new Error(message)))
    );
  }

  private isAccessTokenBypassedAuthEndpoint(url: string): boolean {
    const publicAuthPaths = [
      '/auth/sign-up',
      '/auth/verify-email',
      '/auth/verification/resend',
      '/auth/set-password',
      '/auth/password-reset/request',
      '/auth/login',
      '/auth/passkeys/authentication/options',
      '/auth/passkeys/authentication/verify',
    ];

    return publicAuthPaths.some(path => url.includes(`${API_BASE_URL}${path}`))
      || url.split(/[?#]/)[0] === `${API_BASE_URL}/auth/refresh-access-token`
      || url.includes(`${API_BASE_URL}/dev/qa/personas/login/`);
  }

  private isRefreshOrLogoutEndpoint(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return path === `${API_BASE_URL}/auth/refresh-access-token` ||
      path === `${API_BASE_URL}/auth/refresh-access-token/logout` ||
      path === `${API_BASE_URL}/auth/logout`;
  }

}
